import "bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import "jquery";
import "popper.js";

import * as React from "react";
import * as ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { loadUser, OidcProvider } from "redux-oidc";
import { ConnectedRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import { User, WebStorageStateStore } from "oidc-client";
import configureStore from "./store/configureStore";
import { appAbsoluteBaseUrl, appBaseUrl, setApiConfig } from "./ApiGateway";
import App from "./App";
import { AppConfig } from "./AppConfig";
import { MessageBox, MessageBoxType, Spinner } from "./components/Common";
import oidcConfiguration, { OidcComponentConfig } from "./oidcConfiguration";
import { initializeUserManager } from "./oidcUtilities";

// SignalR
// TODO: consider starting hubs for individual component which requires it
import { connectToSimpleWifiHub } from "./store/SimpleWifiStore/signalRHub";
import { connectToOJEUHub } from "./store/OJEUStore/signalRHub";
import { connectToLogHub } from "./store/LogStore/signalRHub";
import { connectToWorkspaceHub } from "./store/WorkspaceStore/signalRHub";
import { connectToHub, nuheatHub, owd5Hub, senzHub, uwg4Hub } from "./store/LegacyStore/hubs";
import { LegacyThermostatType } from "./store/LegacyStore/models";
import { makeUrlAbsolute } from "./Utility";
import { connectToOJEUMWD3Hub } from "./store/OJEUMWD3Store/signalRHub";

// Create browser history to use in the Redux store
const history = createBrowserHistory({ basename: appBaseUrl });

// Get the application-wide store instance, prepopulating with state from the server where available.
const store: any = configureStore(history);

export const startHubs = (user: User | null) => {
    if (!user) {
        console.log("Can't start hubs as user is NULL");

        return;
    }

    if (user.expired) {
        console.log("User data is expired");

        return;
    }

    console.log(
        `Starting hub connections for user ${user.profile.name} `
        +`(email: ${user.profile.email}, ID: ${user.profile.sub})`);

    connectToSimpleWifiHub(store);
    connectToOJEUHub(store);
    connectToOJEUMWD3Hub(store);
    connectToHub(store, uwg4Hub, LegacyThermostatType.UWG4);
    connectToHub(store, nuheatHub, LegacyThermostatType.NuHeat);
    connectToHub(store, senzHub, LegacyThermostatType.Senz);
    connectToHub(store, owd5Hub, LegacyThermostatType.OWD5);
    connectToLogHub(store);
    connectToWorkspaceHub(store);
};

const ConfigLoaderWrapper = () => {
    const [config, setConfig] = React.useState<AppConfig | null>(null);
    const [configError, setConfigError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const configUrl = appAbsoluteBaseUrl + "config";
        const setGenericLoadError = () => {
            /* TODO: include some info like build number, request ID, etc.*/
            return setConfigError("Application configuration failed. Please contact site owner at cp-team@oj.dk.");
        };

        fetch(configUrl)
            .then(x => x.json()
                .catch(je => {
                    console.error("Failed to parse config JSON", je);
                    setGenericLoadError();
                }))
            .catch(fe => {
                console.error(`Failed to load config JSON from ${configUrl}`, fe);
                setGenericLoadError();
            })
            .then(x => x as AppConfig)
            .then(x => {
                console.debug("Application loaded configuration", x);
                setConfig(x);
            });
    }, [appAbsoluteBaseUrl]);

    if (configError) {
        return (
            <MessageBox
                className="my-5"
                type={MessageBoxType.Error}
                title="Application Configuration Error"
                description={configError || ""}>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => window.location.reload()}>Reload</button>
            </MessageBox>
        );
    }

    if (!config) {
        return <Spinner description="Loading..." />;
    }

    const sanitizeOidcConfig = (oidcConfig: OidcComponentConfig) => ({
        ...oidcConfig,
        redirect_uri: makeUrlAbsolute(appAbsoluteBaseUrl, oidcConfig.redirect_uri),
        post_logout_redirect_uri: makeUrlAbsolute(appAbsoluteBaseUrl, oidcConfig.post_logout_redirect_uri),
        silent_redirect_uri: makeUrlAbsolute(appAbsoluteBaseUrl, oidcConfig.silent_redirect_uri),
        userStore: new WebStorageStateStore({ store: sessionStorage })
    });

    // merge static OIDC component config and config from server
    const oidcCompConfig: OidcComponentConfig = {
        ...oidcConfiguration.config, // <-- default values from static code
        client_id: config.oidc.clientId,
        authority: config.oidc.authority
    };

    const sanitizedOidcConfig = sanitizeOidcConfig(oidcCompConfig);

    setApiConfig(config.api);

    const userManager = initializeUserManager(sanitizedOidcConfig);
    loadUser(store, userManager)
        .then(user => {
            console.debug("Loaded user", user);
        })
        .catch(reason => {
            console.warn("Failed to load user", reason);
        });

    return <Provider store={store}>
        <OidcProvider userManager={userManager} store={store}>
            <ConnectedRouter history={history}>
                <App />
            </ConnectedRouter>
        </OidcProvider>
    </Provider>;
};

ReactDOM.render(<ConfigLoaderWrapper />, document.getElementById("root"));